var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CircularProgress, Typography, useTheme } from '@mui/material';
import { useDinPlayerContext } from 'hx-din-test-2';
import { useNavigator } from 'hx-navigator';
import { Fade, Keypad, SideImageLayout, TripletInput, useKeypad } from 'hx-ui';
import { useCallback, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMount } from 'react-use';
import { useSetProgress } from 'hx-progress';
import { dinTestActions, selectActiveTestStep } from '@/store';
import { DinTestSidePanel } from '@/components/din-test-side-panel.component';
import { useStyledModal } from '@/hooks/use-styled-modal.hook';
import { NavigationButton, NavigationButtonsContainer, } from '@/components/navigation-button.component';
import { CallSupport } from '@/components/call-support.component';
import { KeypadButton } from '@/components/din-test/keypad-button.component';
import { EncouragementNotes } from '@/components/din-test/encouragement-notes.component';
import { StyledProgress } from '@/components/styled-progress.component';
import { useAnalytics } from 'hx-analytics';
import { cn } from 'hx-util';
import { LexieButton } from '@/components/lexie-button.component';
var keys = [
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'Backspace',
    '0',
    'Clear',
];
export function DinTestPage() {
    var theme = useTheme();
    var id = useParams().id;
    var dispatch = useDispatch();
    var _a = useDinPlayerContext(), playTriplet = _a.playTriplet, initialize = _a.initialize, state = _a.state, stop = _a.stop;
    var _b = useState(''), error = _b[0], setError = _b[1];
    var _c = useKeypad(function () { return setError(''); }), value = _c.value, handleChange = _c.handleChange, handleBackspace = _c.handleBackspace, isShaking = _c.isShaking, shake = _c.shake, setValue = _c.setValue, handleClear = _c.handleClear;
    var next = useNavigator({
        phaseLength: 23,
        onComplete: function () {
            dispatch(dinTestActions.processResult());
            dispatch(dinTestActions.sendData());
        },
    }).next;
    var activeStep = useSelector(selectActiveTestStep((id && +id - 1) || 0));
    var sendButtonClick = useAnalytics().sendButtonClick;
    useSetProgress(1, (id && +id) || 0);
    useMount(function () {
        if (!state.initialized)
            initialize();
        dispatch(dinTestActions.initializeTest());
    });
    useEffect(function () {
        dispatch(dinTestActions.setSnr(id));
    }, [id]);
    var playChallenge = useCallback(function () {
        if ((activeStep === null || activeStep === void 0 ? void 0 : activeStep.snr) === undefined)
            return;
        playTriplet(activeStep.challenge, {
            snr: activeStep.snr,
        });
    }, [activeStep]);
    useEffect(function () {
        playChallenge();
    }, [activeStep]);
    var handleSubmit = useCallback(function () {
        if (value.length < 3) {
            setError('Enter at least 3 numbers, then click SUBMIT');
            return shake();
        }
        var response = value;
        dispatch(dinTestActions.submitResponse({ response: response, id: id }));
        setValue([]);
        stop();
        next();
    }, [value, id]);
    // Modals
    var _d = useStyledModal(_jsxs("div", __assign({ className: "flex flex-col gap-10 items-center" }, { children: [_jsx(Typography, __assign({ variant: "h2" }, { children: "Help" })), _jsx(Typography, __assign({ className: "text-lg" }, { children: "We will play 3 numbers with background noise. Enter the numbers you hear in the order you hear them and press SUBMIT." })), _jsx(Typography, __assign({ className: "text-lg" }, { children: "If you can't hear the numbers clearly, enter your best guess." })), _jsx(CallSupport, { onClick: function () {
                    sendButtonClick('test_support');
                }, classes: { textClass: 'flex flex-col' } })] }))), helpModal = _d[0], toggleHelpModal = _d[1], helpModalOpen = _d[2];
    var _e = useStyledModal(_jsxs("div", __assign({ className: "grid gap-4 py-6" }, { children: [_jsx(Typography, __assign({ variant: "h5", className: 'text-xl' }, { children: "Make your best guess..." })), _jsx(Typography, __assign({ variant: "body1", className: 'text-lg' }, { children: "You have not yet submitted your response. If you are unsure, make your best guess." }))] }))), idleModal = _e[0], toggleIdleModal = _e[1], idleModalOpen = _e[2];
    useIdleTimer({
        onIdle: function () {
            if (helpModalOpen || idleModalOpen)
                return;
            toggleIdleModal();
        },
        timeout: 1000 * 15,
    });
    if (!state.initialized)
        return (_jsx("div", __assign({ className: "grid place-items-center min-h-[31.25rem]" }, { children: _jsx(CircularProgress, {}) })));
    return (_jsx(SideImageLayout, { className: 'overflow-y-auto', leftPanel: _jsx(DinTestSidePanel, {}), header: _jsx(StyledProgress, { className: "px-4 py-6", textClassName: 'font-bold' }), content: _jsx(Fade, __assign({ className: "grid gap-4 place-items-center" }, { children: state.error ? (_jsx(ErrorMessage, { action: playChallenge })) : (_jsxs(_Fragment, { children: [_jsxs(Typography, __assign({ variant: "h2", className: "text-3xl md:text-4xl md:font-normal -mt-4" }, { children: ["Step ", id, " of 23"] })), _jsxs("div", __assign({ className: "flex flex-col gap-4" }, { children: [_jsx(TripletInput, { className: "grid-cols-3", value: value, isShaking: isShaking, digitStyle: {
                                    fontSize: '1.875rem',
                                    minHeight: '2em',
                                    borderBottomWidth: '3px',
                                    paddingLeft: '10px',
                                    paddingRight: '10px',
                                    borderBottomColor: theme.palette.primary.main,
                                    '&.has-value': {
                                        borderBottomColor: theme.palette.primary.main,
                                    },
                                } }), _jsx(Keypad, { value: value, onChange: handleChange, onBackspace: handleBackspace, onSubmit: handleSubmit, KeypadButton: KeypadButton, onClear: handleClear, keys: keys })] })), _jsx(Typography, __assign({ className: cn('text-purple pb-4 -mt-3 inline-block h-7', {
                            'text-red': Boolean(error),
                        }) }, { children: Boolean(error) ? _jsx(_Fragment, { children: error }) : _jsx(EncouragementNotes, { id: id }) })), helpModal, idleModal] })) })), nav: _jsxs(NavigationButtonsContainer, __assign({ className: "pb-6" }, { children: [_jsx(NavigationButton, { variant: "outlined", className: "md:hidden block", onClick: toggleHelpModal, label: "Help" }), _jsx(NavigationButton, { onClick: handleSubmit, label: "Submit" })] })) }));
}
var ErrorMessage = function (_a) {
    var action = _a.action;
    return (_jsxs("div", __assign({ className: "grid gap-4 place-items-center" }, { children: [_jsx("div", { children: "Oops! An error occurred while trying to play the audio" }), _jsx(LexieButton, __assign({ onClick: action }, { children: "Retry" }))] })));
};
